(() => {
  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function initFeatureHighlightsCarousel(section) {
    if (!section || section.dataset.fhcReady === 'true') return;

    const viewport = section.querySelector('[data-fhc-viewport]');
    const track = section.querySelector('[data-fhc-track]');
    const prevBtn = section.querySelector('[data-fhc-prev]');
    const nextBtn = section.querySelector('[data-fhc-next]');
    const progressTrack = section.querySelector('[data-fhc-progress-track]');
    const progressThumb = section.querySelector('[data-fhc-progress-thumb]');

    if (!viewport || !track || !prevBtn || !nextBtn) return;

    const cards = () => Array.from(track.querySelectorAll('[data-fhc-card]'));

    function getViewportInlinePadding() {
      const styles = window.getComputedStyle(viewport);
      const pl = Number.parseFloat(styles.paddingLeft || '0') || 0;
      const pr = Number.parseFloat(styles.paddingRight || '0') || 0;
      return { pl, pr };
    }

    function maxScrollLeft() {
      return Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    }

    function getAnchorLeft() {
      const { pl } = getViewportInlinePadding();
      return viewport.scrollLeft + pl;
    }

    function getNextIndex(direction) {
      const list = cards();
      if (!list.length) return 0;

      const anchor = getAnchorLeft();
      const epsilon = 2;

      if (direction > 0) {
        for (let i = 0; i < list.length; i += 1) {
          if (list[i].offsetLeft > anchor + epsilon) return i;
        }
        return list.length - 1;
      }

      for (let i = list.length - 1; i >= 0; i -= 1) {
        if (list[i].offsetLeft < anchor - epsilon) return i;
      }
      return 0;
    }

    function scrollToIndex(rawIndex, smooth) {
      const list = cards();
      if (!list.length) return;

      const index = Math.max(0, Math.min(list.length - 1, rawIndex));
      const behavior = smooth && !prefersReducedMotion() ? 'smooth' : 'auto';
      const { pl } = getViewportInlinePadding();

      viewport.scrollTo({
        left: Math.max(0, list[index].offsetLeft - pl),
        behavior,
      });
    }

    function updateButtons() {
      const max = maxScrollLeft();
      const sl = viewport.scrollLeft;
      const epsilon = 3;

      if (max <= 0) {
        prevBtn.disabled = true;
        nextBtn.disabled = true;
        return;
      }

      prevBtn.disabled = sl <= epsilon;
      nextBtn.disabled = sl >= max - epsilon;
    }

    function updateOverflowNav() {
      const footer = section.querySelector('[data-fhc-footer]');
      if (!footer) return;
      footer.hidden = maxScrollLeft() <= 0;
    }

    function updateProgress() {
      if (!progressTrack || !progressThumb) return;

      const max = maxScrollLeft();
      const trackRect = progressTrack.getBoundingClientRect();
      const tw = trackRect.width;

      if (tw <= 0) return;

      const cs = window.getComputedStyle(progressTrack);
      const padL = Number.parseFloat(cs.paddingLeft || '0') || 0;
      const padR = Number.parseFloat(cs.paddingRight || '0') || 0;
      const innerW = Math.max(0, tw - padL - padR);

      if (max <= 0) {
        progressThumb.style.width = `${Math.min(innerW || tw, 48)}px`;
        progressThumb.style.transform = 'translate3d(0, -50%, 0)';
        return;
      }

      const vw = viewport.clientWidth;
      const sw = viewport.scrollWidth;
      const thumbW = Math.min(Math.max((vw / sw) * innerW, 28), innerW);
      const maxThumbTravel = Math.max(0, innerW - thumbW);
      const x = maxThumbTravel * (viewport.scrollLeft / max);

      progressThumb.style.width = `${thumbW}px`;
      progressThumb.style.transform = `translate3d(${x}px, -50%, 0)`;
    }

    let rafPending = false;
    function syncChrome() {
      if (rafPending) return;
      rafPending = true;
      window.requestAnimationFrame(() => {
        rafPending = false;
        updateButtons();
        updateProgress();
        updateOverflowNav();
      });
    }

    function go(delta) {
      scrollToIndex(getNextIndex(delta), true);
    }

    prevBtn.addEventListener('click', () => go(-1));
    nextBtn.addEventListener('click', () => go(1));

    if (progressTrack) {
      progressTrack.addEventListener('click', (e) => {
        const max = maxScrollLeft();
        if (max <= 0) return;
        const rect = progressTrack.getBoundingClientRect();
        const cs = window.getComputedStyle(progressTrack);
        const padL = Number.parseFloat(cs.paddingLeft || '0') || 0;
        const padR = Number.parseFloat(cs.paddingRight || '0') || 0;
        const innerW = Math.max(1, rect.width - padL - padR);
        const x = e.clientX - rect.left - padL;
        const ratio = Math.max(0, Math.min(1, x / innerW));
        viewport.scrollTo({
          left: ratio * max,
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        });
      });
    }

    viewport.addEventListener('scroll', syncChrome, { passive: true });

    window.addEventListener('resize', syncChrome);

    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(() => syncChrome());
      ro.observe(viewport);
      ro.observe(track);
      if (progressTrack) ro.observe(progressTrack);
    }

    /** Desktop: mouse drag to scroll; touch keeps native pan-x. Threshold avoids fighting text selection. */
    viewport.addEventListener('pointerdown', (downEvent) => {
      if (downEvent.pointerType !== 'mouse' || downEvent.button !== 0) return;

      const pointerId = downEvent.pointerId;
      const startX = downEvent.clientX;
      const startScroll = viewport.scrollLeft;
      let dragging = false;

      const onMove = (ev) => {
        if (ev.pointerId !== pointerId) return;
        const dx = ev.clientX - startX;

        if (!dragging) {
          if (Math.abs(dx) < 6) return;
          dragging = true;
          viewport.classList.add('feature-highlights-carousel__viewport--dragging');
          const sel = window.getSelection?.();
          if (sel && sel.rangeCount > 0) {
            sel.removeAllRanges();
          }
        }

        viewport.scrollLeft = startScroll - dx;
      };

      const onEnd = (ev) => {
        if (ev.pointerId !== pointerId) return;
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onEnd);
        window.removeEventListener('pointercancel', onEnd);
        viewport.classList.remove('feature-highlights-carousel__viewport--dragging');
      };

      window.addEventListener('pointermove', onMove, { passive: true });
      window.addEventListener('pointerup', onEnd);
      window.addEventListener('pointercancel', onEnd);
    });

    viewport.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        go(-1);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        go(1);
      }
    });

    viewport.setAttribute('role', 'region');

    syncChrome();
    section.dataset.fhcReady = 'true';
  }

  function initAll(root = document) {
    root.querySelectorAll('[data-feature-highlights-carousel]').forEach(initFeatureHighlightsCarousel);
  }

  document.addEventListener('DOMContentLoaded', () => initAll());
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
